import { useState } from 'react';
import { FeatureSelect } from 'src/components/FeatureSelect';
import { DataQualityMetricsChart } from 'src/components/DataQualityMetricsChart';
import React from 'react';
import { MetricValueCell } from 'src/components/MetricValueCell';
import {
  ClientSideTable,
  ClientSideTableProps,
} from 'src/components/ClientSideTable';
import { FormField, Select } from '@cloudscape-design/components';

interface FeatureMetricsProps {
  loading: boolean;
  mode: 'numerical' | 'categorical';
  baselineJob?: DataQualityNumericalMetricsDataJob;
  setBaselineJob?: (job: DataQualityNumericalMetricsDataJob) => void;
  data: {
    features: Set<string>;
    jobs:
      | DataQualityNumericalMetricsDataJob[]
      | DataQualityCategoricalMetricsDataJob[];
  } | null;
  selectedFeatures: string[];
  setSelectedFeatures: (features: string[]) => void;
}

export const FeatureMetrics = ({
  loading,
  mode,
  baselineJob,
  setBaselineJob,
  data,
  selectedFeatures,
  setSelectedFeatures,
}: FeatureMetricsProps) => {
  const [selectedMetric, setSelectedMetric] = useState(
    mode === 'numerical' ? 'min' : 'missingPercentage',
  );

  if (!data?.features || data.features.size === 0) {
    return <p className="py-20">No Features of this type detected.</p>;
  }

  const tableColumns: ClientSideTableProps['columnDefinitions'] = [
    ...Array.from(selectedFeatures).reduce<
      ClientSideTableProps['columnDefinitions']
    >((acc, curr) => {
      // @ts-expect-error
      acc.push({
        header: curr,
        id: curr,
        cell: (item: any) => {
          return selectedMetric === 'valueCount' ? (
            <div>
              {item?.[curr]?.[selectedMetric].map((item: any, i: number) => (
                <p key={i}>{item}</p>
              ))}
            </div>
          ) : (
            <MetricValueCell value={item?.[curr]?.[selectedMetric]} />
          );
        },
      });
      return acc;
    }, []),
  ];

  const tableOptions =
    mode === 'numerical'
      ? [
          { label: 'min', value: 'min' },
          { label: 'mean', value: 'mean' },
          { label: 'sum', value: 'sum' },
          { label: 'max', value: 'max' },
          { label: 'Standard Deviation', value: 'stdDev' },
          { label: 'Missing Percentage', value: 'missingPercentage' },
        ]
      : [
          { label: 'Missing Percentage', value: 'missingPercentage' },
          { label: 'Value Counts', value: 'valueCount' },
        ];

  return (
    <div>
      <div className="mt-10 mb-5 flex justify-between items-end flex-wrap gap-2">
        {setBaselineJob && (
          <div className="relative">
            <div className="min-w-80">
              <FormField
                label="Baseline Job"
                constraintText="The selected baseline job will have it's feature's metrics graphed as dashed lines for easy comparison.
                The job will also be highlighted in the table below."
              >
                <Select
                  filteringType="auto"
                  selectedOption={{
                    label: baselineJob?.jobName ?? '',
                    value: baselineJob?.jobName ?? '',
                  }}
                  onChange={(e) => {
                    const job = data?.jobs.find(
                      (job) => job.jobName === e.detail.selectedOption.value,
                    );
                    // @ts-expect-error
                    if (job) setBaselineJob(job);
                  }}
                  placeholder="Select a job"
                  options={data?.jobs.map((job) => ({
                    label: `${job.jobName} - ${job.jobType}`,
                    value: job.jobName,
                  }))}
                />
              </FormField>
            </div>
          </div>
        )}
        <div className="flex gap-2 items-center">
          <span className="font-normal mb-0">
            <span className="font-bold">{selectedFeatures.length}</span> /{' '}
            <span className="font-bold">{data.features.size}</span> features
            selected
          </span>
          <FeatureSelect
            setSelectedFeatures={setSelectedFeatures}
            featureList={Array.from(data?.features ?? [])}
          />
        </div>
      </div>

      <DataQualityMetricsChart
        baselineJob={baselineJob ?? undefined}
        mode={mode}
        data={data ?? { features: new Set(), jobs: [] }}
        loading={loading}
        selectedFeatures={selectedFeatures}
      />
      <div className="mt-16">
        <ClientSideTable
          key={selectedFeatures.toString()}
          highlightedRow={
            baselineJob
              ? {
                  columnId: 'jobName',
                  value: baselineJob.jobName,
                }
              : undefined
          }
          title="Metrics by Job"
          actions={
            <div className="flex gap-4 items-end">
              <div className="min-w-32 relative">
                <FormField label="Metric">
                  <Select
                    selectedOption={{
                      label: selectedMetric,
                      value: selectedMetric,
                    }}
                    options={tableOptions}
                    onChange={(e) => {
                      if (e.detail.selectedOption.value)
                        setSelectedMetric(e.detail.selectedOption.value);
                    }}
                  />
                </FormField>
              </div>
              <span className="font-normal mb-2 ml-4">
                <span className="font-bold">{selectedFeatures.length}</span> /{' '}
                <span className="font-bold">{data.features.size}</span> features
                selected
              </span>
              <FeatureSelect
                setSelectedFeatures={setSelectedFeatures}
                featureList={Array.from(data?.features ?? [])}
              />
            </div>
          }
          columnDefinitions={[
            {
              header: 'Job Name',
              id: 'jobName',
              cell: (item) => item.jobName,
            },
            {
              header: 'Job Type',
              id: 'jobType',
              sortingField: 'jobType',
              cell: (item) => item.jobType,
            },
            {
              id: 'createdDate',
              header: 'Created Date',
              sortingField: 'createdDate',
              cell: (item) => {
                const date = new Date(parseInt(item.createdDate) * 1000);
                return date.toUTCString();
              },
            },
            ...tableColumns,
          ]}
          data={data?.jobs ?? []}
          error={null}
        />
      </div>
    </div>
  );
};
